import { useState } from 'react';
import { Home, Lock, ShieldCheck, Users } from 'lucide-react';
import { canRead, type AccessFields, type Audience, type FamilyMember } from '@app/shared';
import { useFamily } from '../platform/session.tsx';
import { MemberPicker, Avatar } from './People.tsx';
import { Sheet } from './Sheet.tsx';

const ICONS: Record<Audience, typeof Lock> = { private: Lock, selected: Users, adults: ShieldCheck, family: Home };

export interface AudienceValue {
  audience: Audience;
  sharedWith: string[];
}

/**
 * The four sharing choices, explained in plain words. Shown wherever
 * something new can be created.
 */
export function AudiencePicker({ value, onChange, familyName }: { value: AudienceValue; onChange(v: AudienceValue): void; familyName: string }) {
  const { members, actor, isChild } = useFamily();
  const others = members.filter((m) => m.userId !== actor.userId);
  const options: { value: Audience; label: string; desc: string }[] = [
    { value: 'private', label: 'Only me', desc: 'Nobody else can see this.' },
    { value: 'selected', label: 'Selected people', desc: 'You and the people you choose.' },
    ...(isChild ? [] : [{ value: 'adults' as const, label: 'Grown-ups', desc: 'All adults in the family. Not children.' }]),
    { value: 'family', label: 'Everyone', desc: `Everyone in ${familyName}.` },
  ];
  return (
    <div className="ui-audience" role="radiogroup" aria-label="Who can see this">
      {options.map((o) => {
        const Icon = ICONS[o.value];
        const checked = value.audience === o.value;
        return (
          <button
            key={o.value}
            type="button"
            role="radio"
            aria-checked={checked}
            className="ui-audience__option"
            onClick={() => onChange({ audience: o.value, sharedWith: o.value === 'selected' ? value.sharedWith : [] })}
          >
            <Icon aria-hidden="true" />
            <span className="ui-audience__text">
              <span className="ui-audience__label">{o.label}</span>
              <span className="ui-audience__desc">{o.desc}</span>
            </span>
          </button>
        );
      })}
      {value.audience === 'selected' && (
        <div style={{ paddingTop: 'var(--space-2)' }}>
          {others.length === 0 ? (
            <p className="muted">Invite family members first to share with specific people.</p>
          ) : (
            <MemberPicker
              label="Share with"
              members={others}
              selected={value.sharedWith}
              onChange={(ids) => onChange({ audience: 'selected', sharedWith: ids })}
            />
          )}
        </div>
      )}
    </div>
  );
}

/** Viewer-aware short label: "Only me", "You and Sam", "Grown-ups", "Everyone". */
export function audienceLabel(rec: Pick<AccessFields, 'audience' | 'sharedWith' | 'ownerId'>, viewerId: string, nameOf: (id: string) => string): string {
  const mine = rec.ownerId === viewerId;
  switch (rec.audience) {
    case 'private':
      return mine ? 'Only me' : 'Private';
    case 'adults':
      return 'Grown-ups';
    case 'family':
      return 'Everyone';
    case 'selected': {
      const people = [...(rec.ownerId && !mine ? [rec.ownerId] : []), ...rec.sharedWith.filter((id) => id !== viewerId)];
      const names = people.map(nameOf);
      if (names.length === 0) return mine ? 'Only me' : 'You';
      const lead = 'You and ';
      return names.length <= 2 ? `${lead}${names.join(' & ')}` : `${lead}${names.length} others`;
    }
  }
}

/**
 * Compact badge that answers "who can see this?" at a glance; tapping it
 * lists exactly who has access.
 */
export function AudienceBadge({ record, compact }: { record: AccessFields & { id: string }; compact?: boolean }) {
  const { actor, members, nameOf } = useFamily();
  const [open, setOpen] = useState(false);
  const Icon = ICONS[record.audience];
  const label = audienceLabel(record, actor.userId, nameOf);
  const viewers: FamilyMember[] = members.filter((m) => canRead({ userId: m.userId, role: m.role }, record));
  return (
    <>
      <button
        type="button"
        className="ui-badge ui-badge--button"
        onClick={(e) => {
          e.stopPropagation();
          setOpen(true);
        }}
        aria-label={`Visible to: ${label}. Show who can see this.`}
      >
        <Icon aria-hidden="true" />
        {!compact && label}
      </button>
      <Sheet open={open} onClose={() => setOpen(false)} title="Who can see this">
        <div className="ui-stack">
          <p>
            <strong>{label}.</strong> {record.audience === 'private' && 'Only the person who created it can see it.'}
            {record.audience === 'selected' && 'Only the people listed below.'}
            {record.audience === 'adults' && 'All adults in the family. Children cannot see it.'}
            {record.audience === 'family' && 'Everyone in the family, including children.'}
          </p>
          <ul className="ui-list ui-card" style={{ padding: 0 }}>
            {viewers.map((m) => (
              <li key={m.userId} className="ui-row">
                <Avatar member={m} />
                <span className="ui-row__main">
                  <span className="ui-row__title">
                    {m.displayName}
                    {m.userId === actor.userId ? ' (you)' : ''}
                  </span>
                  <span className="ui-row__meta">{m.userId === record.ownerId ? 'Owner' : m.role === 'child' ? 'Child' : 'Adult'}</span>
                </span>
              </li>
            ))}
          </ul>
          <p className="muted" style={{ fontSize: 'var(--text-sm)' }}>
            Being in the same family never shares something by itself — only these settings do.
          </p>
        </div>
      </Sheet>
    </>
  );
}
